import { Auth } from "aws-amplify"
import axios from "axios"
import { appLogger } from "@helpers/logger"
import { dispatch } from "@redux/store"
import { canonicalAdminRole, groupsFromJwtPayload } from "@configs/adminRoles"

/** flag represent different user auth status */
const AuthStatus = {
  AUTHENTIFICATED: "AUTHENTIFICATED",
  UNAUTHENTIFICATED: "UNAUTHENTIFICATED",
  REQUIREDCHALLENGE: "REQUIREDCHALLENGE"
}

/** all Aws Cognito  auth event  (from Hub) */
const AuthEvent = {
  SIGNIN: "signIn",
  SIGNOUT: "signOut",
  TOKEN_REFRESH: "tokenRefresh"
}
/** storage keys for Aqarco */
const baselocalStorageKey = "aws-cognito"
const localStorageUserKey = `${baselocalStorageKey}-user`

const AuthChallenge = {
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
  NO_CHALLENGE: "NO_CHALLENGE"
}

/** handle user sign in via cognito  */
const signInUser = async ({ username, password }) => {
  const useV2 = String(process.env.REACT_APP_USE_V2_AUTH || "false") === "true"
  if (!useV2) {
    return await Auth.signIn(username, password)
  }

  const baseUrl = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_V2_BACKEND_URL ||
    "http://localhost:3000"
  ).replace(/\/+$/, "")
  const response = await axios.post(`${baseUrl}/auth/admin/login`, {
    email: username,
    password
  })
  const data = response.data || {}
  const accessToken = data.accessToken || ""
  const refreshToken = data.refreshToken || ""
  const email = data?.user?.email || username
  const role = data?.user?.role || "admin"
  const adminRole = canonicalAdminRole(data?.user?.adminRole || data?.user?.admin_role)
  const abilityGroups = groupsFromJwtPayload({ adminRole })
  const payload = {
    email,
    role,
    adminRole,
    "cognito:groups": abilityGroups
  }

  localStorage.setItem("accessToken", accessToken)
  localStorage.setItem("refreshToken", refreshToken)
  localStorage.setItem("keyprefix", "v2local")
  localStorage.setItem("v2local.LastAuthUser", email)
  localStorage.setItem(`v2local.${email}.idToken`, accessToken)

  return {
    username: email,
    keyPrefix: "v2local",
    signInUserSession: {
      idToken: {
        jwtToken: accessToken,
        payload
      },
      refreshToken: {
        token: refreshToken
      }
    },
    attributes: {
      email
    }
  }
}

const completeNewPassword = async ({ user, password }) =>
  await Auth.completeNewPassword(user, password)

const handleAuthChange = (payload, setState, setUser) => {
  appLogger.info(`event : ${payload.event}`, "[AuthContext][handleAuthChange]")

  const { event, data } = payload

  switch (event) {
    case AuthEvent.SIGNIN:
      appLogger.info(
        `user: ${data.username}`,
        `[AuthContext][handleAuthChange][event:${event}]`
      )

      const user = {
        attributes: data.attributes,
        session: data.signInUserSession,
        username: data.username
      }
      setState(AuthStatus.AUTHENTIFICATED)
      setUser(user)
      localStorage.setItem(localStorageUserKey, JSON.stringify(user))
      break

    case AuthEvent.SIGNOUT:
      appLogger.info(
        `user signout`,
        `[AuthContext][handleAuthChange][event:${event}]`
      )

      setState(AuthStatus.UNAUTHENTIFICATED)
      setUser(null)
      localStorage.removeItem(localStorageUserKey)
      dispatch.auth.handleSignout()
      break
    case AuthEvent.TOKEN_REFRESH:
      window.location.reload()
      break

    default:
      break
  }
}
/** handle user sign out via cognito  */
const signOutUser = async () => {
  const useV2 = String(process.env.REACT_APP_USE_V2_AUTH || "false") === "true"
  if (!useV2) {
    return await Auth.signOut()
  }
  const prefix = localStorage.getItem("keyprefix") || "v2local"
  const idUser = localStorage.getItem(`${prefix}.LastAuthUser`) || ""
  localStorage.removeItem("accessToken")
  localStorage.removeItem("refreshToken")
  localStorage.removeItem("keyprefix")
  localStorage.removeItem(`${prefix}.LastAuthUser`)
  if (idUser) localStorage.removeItem(`${prefix}.${idUser}.idToken`)
}
export const CognitoAuth = {
  AuthStatus,
  AuthEvent,
  signOutUser,
  AuthChallenge,
  handleAuthChange,
  completeNewPassword,
  signInUser
}
